import { useState, useCallback } from 'react';
import { useQueryClient } from 'react-query';
import useHttpMutation from '@actions/services/firestore/browser/useHttpMutation';
import type {
    UseUpdateFolderResult,
    UseUpdateFolderParams,
    UpdateFolderChanges,
    Folder,
    UpdateFolderResponse,
} from './types';

const useUpdateFolder = ({ agentId, entityType }: UseUpdateFolderParams): UseUpdateFolderResult => {
    const queryClient = useQueryClient();
    const [error, setError] = useState<Error | undefined>();
    const [isUpdating, setIsUpdating] = useState(false);

    const { mutateAsync } = useHttpMutation<{ changes: UpdateFolderChanges }, UpdateFolderResponse>(
        {
            method: 'PATCH',
            onSuccess: () => {
                queryClient.invalidateQueries({ queryKey: [`folders-${agentId}-${entityType}`] });
                // Also invalidate workflows query since cascading rename updates workflow folderPaths
                queryClient.invalidateQueries({ queryKey: [`workflows-${agentId}`] });
                queryClient.invalidateQueries({ queryKey: [`prompts-${agentId}`] });
            },
            onError: (err: Error) => {
                setError(err);
            },
        }
    );

    const updateFolder = useCallback(
        async (folderId: string, changes: UpdateFolderChanges): Promise<Folder> => {
            setError(undefined);
            setIsUpdating(true);
            try {
                const response = await mutateAsync({
                    pathname: `/folders/${folderId}`,
                    body: { changes },
                });
                return response.data;
            } finally {
                setIsUpdating(false);
            }
        },
        [mutateAsync]
    );

    return {
        updateFolder,
        isUpdating,
        error,
    };
};

export default useUpdateFolder;
